import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, TrendingDown, ArrowRight, Loader2, Target } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import BudgetCapModal from "@/components/dashboard/BudgetCapModal";

/**
 * AI-generated dashboard card comparing this month's category spend vs last
 * month's. Powered by GET /api/insights/spending-shift (Claude Sonnet 4.5 with
 * a heuristic fallback). Renders the two-line headline+subtext plus the top-3
 * category shifts with signed deltas.
 *
 * Silent on error — this is an enhancement, not a blocker. The dashboard
 * should never break because the LLM key is missing or an API error slipped
 * through.
 */
export default function SpendingShiftCard({ currency = "USD" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capTarget, setCapTarget] = useState(null); // {category, suggested}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/insights/spending-shift");
        if (!cancelled) setData(data);
      } catch (e) {
        console.debug("spending shift insight fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card-flat p-5 mb-6 flex items-center gap-2 text-sm text-[var(--text-secondary)]" data-testid="spending-shift-loading">
        <Loader2 size={14} className="animate-spin" />
        Analysing your spending shift…
      </div>
    );
  }

  // Nothing to compare (fresh account with no history in either month)
  if (!data || (!data.current_total && !data.prev_total)) return null;

  const totalPct = Number(data.total_delta_pct || 0);
  const spentMore = totalPct > 0;
  const flat = totalPct === 0;
  const monthLabel = new Date(`${data.month}-01T00:00:00`)
    .toLocaleString(undefined, { month: "long" });
  const prevMonthLabel = new Date(`${data.prev_month}-01T00:00:00`)
    .toLocaleString(undefined, { month: "long" });

  const badgeTone = flat
    ? "bg-[var(--bg-secondary)] text-[var(--text-secondary)]"
    : spentMore
      ? "bg-[rgba(196,91,77,0.12)] text-[var(--accent-danger)]"
      : "bg-[rgba(45,90,64,0.12)] text-[var(--accent-positive)]";

  const Icon = flat ? Sparkles : spentMore ? TrendingUp : TrendingDown;

  return (
    <div className="card-flat p-5 mb-6" data-testid="spending-shift-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`p-2 rounded-lg ${badgeTone}`}>
            <Icon size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="label-eyebrow" style={{ color: "var(--accent-primary)" }}>
                <Sparkles size={11} className="inline mr-1 -mt-0.5" />
                AI insight · {prevMonthLabel} → {monthLabel}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeTone}`} data-testid="spending-shift-delta-pct">
                {spentMore ? "+" : ""}{totalPct.toFixed(0)}%
              </span>
            </div>
            <div className="text-base font-semibold mt-1.5 text-[var(--text-primary)]" data-testid="spending-shift-headline">
              {data.headline}
            </div>
            {data.subtext && (
              <div className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed" data-testid="spending-shift-subtext">
                {data.subtext}
              </div>
            )}
          </div>
        </div>
        <Link
          to="/reports"
          className="btn-ghost text-xs shrink-0"
          data-testid="spending-shift-cta"
        >
          Open Reports <ArrowRight size={13} />
        </Link>
      </div>

      {Array.isArray(data.top_categories) && data.top_categories.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--border-default)] grid grid-cols-1 sm:grid-cols-3 gap-3">
          {data.top_categories.map(c => {
            const up = c.delta_amount > 0;
            const flatCat = c.delta_amount === 0;
            const color = flatCat
              ? "var(--text-secondary)"
              : up ? "var(--accent-danger)" : "var(--accent-positive)";
            // Only show the "Set cap" affordance when spending went UP — that's
            // the actionable case. Suggested cap = previous-month baseline.
            const showSetCap = up && c.previous > 0;
            return (
              <div
                key={c.category}
                className="flex flex-col gap-1.5 p-2 rounded-lg hover:bg-[var(--bg-secondary)]/50 transition-colors"
                data-testid={`spending-shift-cat-${c.category}`}
              >
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.category}</div>
                    <div className="text-xs text-[var(--text-muted)] num-md">
                      {formatMoney(c.previous, currency)} → {formatMoney(c.current, currency)}
                    </div>
                  </div>
                  <div className="text-right shrink-0" style={{ color }}>
                    <div className="text-xs font-semibold">
                      {up ? "+" : ""}{Number(c.delta_pct).toFixed(0)}%
                    </div>
                    <div className="text-xs num-md">
                      {up ? "+" : ""}{formatMoney(c.delta_amount, currency)}
                    </div>
                  </div>
                </div>
                {showSetCap && (
                  <button
                    type="button"
                    onClick={() => setCapTarget({ category: c.category, suggested: c.previous })}
                    className="text-xs font-medium flex items-center justify-center gap-1 py-1.5 rounded-md border border-dashed transition-all"
                    style={{
                      borderColor: "var(--accent-primary)",
                      color: "var(--accent-primary)",
                    }}
                    data-testid={`set-cap-btn-${c.category}`}
                  >
                    <Target size={11} /> Set a {c.category} cap
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {capTarget && (
        <BudgetCapModal
          category={capTarget.category}
          suggested={capTarget.suggested}
          currency={currency}
          onClose={() => setCapTarget(null)}
          onSaved={() => setCapTarget(null)}
        />
      )}
    </div>
  );
}
